import type { ReactNode } from 'react'
import { PANEL } from '@/components/budget/MonthlyBudgets'
import { cn } from '@/lib/utils'

export type SettingsSectionId = 'you' | 'household' | 'plan' | 'data' | 'danger'

export function SettingsPanel({
  id,
  title,
  aside,
  danger,
  className,
  overlays,
  children,
}: {
  id: SettingsSectionId
  title: string
  aside?: ReactNode
  danger?: boolean
  className?: string
  /** Sheets and dialogs the panel opens — kept out of the row list so they don't get a divider. */
  overlays?: ReactNode
  children: ReactNode
}) {
  const headingId = `settings-${id}-title`
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn(
        PANEL,
        'scroll-mt-4',
        danger && 'border-transparent bg-[var(--fl-error-bg)]',
        className
      )}
    >
      <div className="flex items-baseline justify-between gap-3 pt-3.5 pb-1.5">
        <h3
          id={headingId}
          className={cn(
            'text-[15px] font-semibold',
            danger ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
          )}
        >
          {title}
        </h3>
        {aside && <span className="text-xs text-[var(--fl-text-2)]">{aside}</span>}
      </div>
      <div className="divide-y divide-[var(--fl-divider)]">{children}</div>
      {overlays}
    </section>
  )
}

/** One setting: its name (and a hint), the current value, and what you can do about it. */
export function SettingsRow({
  label,
  hint,
  value,
  stack,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  value?: ReactNode
  /** Phone: put the control on its own line under the label (for wide controls). */
  stack?: boolean
  children?: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex min-h-[52px] items-center gap-3 py-2.5 text-sm',
        stack && 'flex-wrap sm:flex-nowrap'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[var(--fl-text-1)]">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-[var(--fl-text-2)]">{hint}</div>}
      </div>
      {value != null && (
        <div className="min-w-0 truncate text-right text-[13.5px] text-[var(--fl-text-2)]">
          {value}
        </div>
      )}
      {children && (
        <div className={cn('flex shrink-0 items-center gap-2', stack && 'w-full sm:w-auto')}>
          {children}
        </div>
      )}
    </div>
  )
}

export const ROW_BUTTON = 'h-8 rounded-lg px-3 text-[13px] font-medium'
export const DANGER_BUTTON =
  'border-[var(--fl-error)] bg-[var(--fl-surface)] text-[var(--fl-error)] hover:bg-[var(--fl-error-bg)]'
